<script setup lang="ts">
import { Form } from '@primevue/forms';
import type { FormSubmitEvent } from '@primevue/forms/form';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { CircleX, LoaderCircle, LogIn } from 'lucide-vue-next';
import { FloatLabel, InputText, Password } from 'primevue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';
import { login as loginRequest, LoginResult } from '@api/login';
import LegacyClassModifier from '@modules/legacy-class-modifier';

const { t } = useI18n();

const ERROR_MESSAGES: Record<number, string> = {
    [LoginResult.INCORRECT_USERNAME]: 'error.invalidEmailOrPassword',
    [LoginResult.INCORRECT_PASSWORD]: 'error.invalidPasswordAttemptsRemaining',
    [LoginResult.INCORRECT_COMBINATION]: 'error.invalidEmailOrPassword',
    [LoginResult.ACCOUNT_NOT_ACTIVATED]: 'error.accountNotActivated',
    [LoginResult.ACCOUNT_LOCKED]: 'error.accountLocked',
    [LoginResult.SYSTEM_MAINTENANCE]: 'error.systemMaintenance'
} as const;

const props = defineProps<{
    email?: string;
    isLegacy?: boolean;
    loginResponseHandler: (loginResult: number) => boolean;
    readOnlyEmail?: boolean;
    showBackToLogin?: boolean;
    showSignUp: boolean;
}>();

let accountLockoutInterval: ReturnType<typeof setInterval> | undefined;
const requestErrorMessage = ref('');
const requestLoading = ref(false);

const onFormSubmit = async (event: FormSubmitEvent) => {
    clearInterval(accountLockoutInterval);

    requestErrorMessage.value = '';

    if (!event.valid) return;

    requestLoading.value = true;
    const requestResult = await loginRequest(
        props.readOnlyEmail ? props.email : event.values.email,
        event.values.password
    );

    if (props.loginResponseHandler(requestResult?.loginResult)) return;

    requestLoading.value = false;
    requestErrorMessage.value = t('error.pleaseTryAgain');
    if (requestResult?.loginResult !== undefined && ERROR_MESSAGES[requestResult.loginResult] !== undefined) {
        if (requestResult?.loginResult === LoginResult.INCORRECT_PASSWORD) {
            requestErrorMessage.value = t(
                ERROR_MESSAGES[requestResult.loginResult],
                requestResult.attemptsBeforeLockout ?? 0
            );
            return;
        }

        if (requestResult?.loginResult === LoginResult.ACCOUNT_LOCKED) {
            if (requestResult.lockoutDate === undefined) {
                setStandardAccountLockoutRequestErrorMessage();
                return;
            }

            startAccountLockoutInterval(requestResult.lockoutDate);
            return;
        }

        requestErrorMessage.value = t(ERROR_MESSAGES[requestResult.loginResult]);
    }
};

const startAccountLockoutInterval = (lockoutDate: Date): void => {
    setAccountLockoutRequestErrorMessage(secondsUntil(lockoutDate));

    accountLockoutInterval = setInterval(() => {
        const secondsRemaining = secondsUntil(lockoutDate);

        if (secondsRemaining <= 0) {
            setStandardAccountLockoutRequestErrorMessage();
            clearInterval(accountLockoutInterval);
            return;
        }
        setAccountLockoutRequestErrorMessage(secondsRemaining);
    }, 1000);
};

const setAccountLockoutRequestErrorMessage = (secondsRemaining: number) => {
    requestErrorMessage.value = t('error.accountLockedTimeRemaining', {
        minutes: Math.floor(secondsRemaining / 60),
        seconds: secondsRemaining % 60
    });
};

const secondsUntil = (futureDate: Date) => {
    return Math.floor((futureDate.getTime() - new Date().getTime()) / 1000);
};

const setStandardAccountLockoutRequestErrorMessage = () => {
    requestErrorMessage.value = t(ERROR_MESSAGES[LoginResult.ACCOUNT_LOCKED]);
};

const resolver = zodResolver(
    z.object({
        email: props.readOnlyEmail
            ? z.null()
            : z
                  .string({ message: t('error.requiredField') })
                  .trim()
                  .nonempty({ message: t('error.requiredField') })
                  .pipe(
                      z.email({
                          message: t('error.invalidEmailAddress'),
                          pattern: z.regexes.unicodeEmail
                      })
                  ),
        password: z.string({ message: t('error.requiredField') }).nonempty({
            message: t('error.invalidPassword')
        })
    })
);

const legacyClassModifier = props.isLegacy ? LegacyClassModifier : (cssClasses: string) => cssClasses;
</script>

<template>
    <Form
        v-slot="$form"
        :aria-label="$t('ariaLabel.loginForm')"
        :class="legacyClassModifier('flex flex-col gap-4')"
        :resolver="resolver"
        @submit="onFormSubmit">
        <div :class="legacyClassModifier('flex flex-col gap-1')">
            <FloatLabel variant="on">
                <InputText
                    autocomplete="username"
                    :aria-label="$t('ariaLabel.emailInput')"
                    :disabled="readOnlyEmail || requestLoading"
                    inputId="email"
                    name="email"
                    type="text"
                    :defaultValue="props.email"
                    fluid />
                <label for="email">{{ $t('email') }}</label>
            </FloatLabel>
            <Message
                v-if="$form.email?.invalid"
                :aria-label="$t('ariaLabel.errorMessage')"
                severity="error"
                size="small"
                variant="simple"
                >{{ $form.email.error.message }}</Message
            >
        </div>
        <div :class="legacyClassModifier('flex flex-col gap-1')">
            <FloatLabel variant="on">
                <Password
                    :aria-label="$t('ariaLabel.passwordInput')"
                    :disabled="requestLoading"
                    :feedback="false"
                    inputId="password"
                    name="password"
                    :pt="{
                        pcInputText: {
                            root: { autocomplete: 'current-password' }
                        }
                    }"
                    fluid />
                <label for="password">{{ $t('password') }}</label>
            </FloatLabel>
            <Message
                v-if="$form.password?.invalid"
                :aria-label="$t('ariaLabel.errorMessage')"
                severity="error"
                size="small"
                variant="simple"
                >{{ $form.password.error.message }}</Message
            >
        </div>
        <Message v-if="!!requestErrorMessage" :aria-label="$t('ariaLabel.errorMessage')" severity="error">
            <template #icon><CircleX /></template>
            <template #default>{{ requestErrorMessage }}</template>
        </Message>
        <div :class="legacyClassModifier('flex flex-col-reverse gap-4 md:flex-row justify-center md:justify-between')">
            <Button
                v-if="showBackToLogin"
                :as="requestLoading ? 'button' : 'a'"
                :disabled="requestLoading"
                :class="legacyClassModifier('w-full md:w-auto')"
                href="/"
                severity="secondary"
                size="small"
                :label="$t('backToLogin')"
                variant="text" />
            <Button
                v-else
                :as="requestLoading ? 'button' : 'a'"
                :aria-label="$t('ariaLabel.forgottenPasswordButton')"
                :disabled="requestLoading"
                :class="legacyClassModifier('w-full md:w-auto')"
                href="#forgotten-password"
                severity="secondary"
                size="small"
                :label="$t('forgottenPasswordQuestion')"
                variant="text" />
            <Button
                :aria-label="$t('ariaLabel.loginButton')"
                :disabled="requestLoading"
                :label="$t('login')"
                type="submit">
                <template #icon>
                    <LoaderCircle v-if="requestLoading" :class="legacyClassModifier('animate-spin')" />
                    <LogIn v-else />
                </template>
            </Button>
        </div>
        <div v-if="props.showSignUp" :class="legacyClassModifier('flex gap-2 items-center justify-center md:mt-5')">
            <span>{{ $t('dontHaveAnAccount') }}</span>
            <Button
                :as="requestLoading ? 'button' : 'a'"
                :disabled="requestLoading"
                href="#sign-up"
                size="small"
                severity="secondary"
                variant="text"
                >{{ $t('signUp') }}</Button
            >
        </div>
    </Form>
</template>
